/**
 * Loading, failed and empty — the three states a screen must not skip (Batch 1).
 *
 * ---------------------------------------------------------------------------
 * A FAILED REQUEST AND AN EMPTY RESULT LOOK IDENTICAL, AND THEY MEAN OPPOSITE THINGS
 * ---------------------------------------------------------------------------
 * Five screens called useApi and destructured neither `loading` nor `error`, so a
 * slow load rendered as "nothing here" and a failed one rendered as "nothing
 * here" — a positive claim, made confidently, by a component that did not know.
 * That is the bug that had the Email chip reading 0 against a 404 during a
 * Railway deploy, and it was sitting on Accounting, Dispatch, Calendar,
 * Follow-Ups and Activity Stats at the same time.
 *
 * The distinction these three components exist to preserve:
 *   Loading   we do not know yet — say so, claim nothing
 *   LoadError we asked and failed — say that, and offer to ask again
 *   Empty     we asked, it worked, there is genuinely nothing
 *
 * ---------------------------------------------------------------------------
 * ONLY LoadError CARRIES A RETRY, AND IT IS NEVER AUTOMATIC
 * ---------------------------------------------------------------------------
 * A screen that retried on its own would turn one failed request into a loop
 * against a server that is already struggling, and the person watching would see
 * a flicker with no way to tell whether anything was happening. A button says
 * what went wrong and hands the decision over.
 */

import React from 'react';
import { CRM } from '../lib/theme';
import { Icons } from '../lib/icons';

const C = { ...CRM };

const box: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '10px',
  borderRadius: '8px', padding: '14px 16px', fontSize: '12px', lineHeight: 1.5,
};

/** We do not know yet. Deliberately quiet — it is usually gone in 200ms. */
export function Loading({ what = 'Loading', inline }: { what?: string; inline?: boolean }) {
  return (
    <div style={{ ...box, color: C.muted, justifyContent: inline ? 'flex-start' : 'center', padding: inline ? '10px 0' : '28px 16px' }}>
      {what}…
    </div>
  );
}

/**
 * We asked and it failed.
 *
 * `what` names the thing that did not load, because "Something went wrong" on a
 * page with six panels tells the reader nothing about which one to distrust — and
 * the rest of the page is still showing real data they might otherwise act on.
 */
export function LoadError({ what, error, onRetry }: { what: string; error: string; onRetry?: () => void }) {
  return (
    <div style={{ ...box, background: `${C.amberTint}`, border: `1px solid ${C.amberSoftLine}`, color: C.amber, flexWrap: 'wrap' }}>
      <Icons.warning size={15} />
      <span style={{ flex: 1, minWidth: '180px' }}>
        <strong>{what}</strong> could not be loaded, so what you see is incomplete. ({error})
      </span>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{ padding: '4px 11px', background: C.panel2, border: `1px solid ${C.amberLine}`, color: C.amber, borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}
        >
          Try again
        </button>
      )}
    </div>
  );
}

/** We asked, it worked, there is nothing. A statement, not an absence. */
export function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ ...box, color: C.dim, justifyContent: 'center', padding: '26px 16px', textAlign: 'center' }}>{children}</div>;
}

/**
 * The common shape: gate a panel on ONE source, and let everything else on the
 * page keep rendering.
 *
 * Returns null when there is nothing to say, so a caller writes
 * `{gate(...) ?? <TheRealThing/>}` and does not have to think about the order.
 *
 * `hasData` matters: a REFETCH sets loading true while real data is still on
 * screen, and replacing a populated panel with "Loading…" on every refresh is
 * worse than leaving the numbers up for a moment.
 */
export function gate(
  what: string,
  loading: boolean,
  error: string | null,
  hasData: boolean,
  onRetry?: () => void
): React.ReactElement | null {
  if (error) return <LoadError what={what} error={error} onRetry={onRetry} />;
  if (loading && !hasData) return <Loading what={`Loading ${what.toLowerCase()}`} />;
  return null;
}
